import { css, SerializedStyles } from '@emotion/core'
import { useLocation } from '@reach/router'
import parse from 'html-react-parser'
import React from 'react'

import Image from '../components/Image'
import { breakpoints } from '../constants/breakPoints'
import { getFooterIntroData, getFooterMenuItems, getFooterOutroData } from '../constants/footer'
import { useLanguage } from '../hooks/useLanguage'
import { useSiteMetadata } from '../hooks/useSiteMetadata'
import LinkedinIcon from '../icons/social/logo-linkedin.svg'
import YouTubeIcon from '../icons/social/logo-youtube.svg'
import { Link } from './Link'
import VerticalTextDivider from './VerticalTextDivider'

interface FooterProps {
  blackBG?: boolean
  withoutBadgeStrip?: boolean
  withoutNavigation?: boolean
  cssStyles?: SerializedStyles
}

const Footer: React.FunctionComponent<FooterProps> = ({
  blackBG = false,
  withoutBadgeStrip = false,
  withoutNavigation = false,
  cssStyles,
}) => {
  const { contactEmail } = useSiteMetadata()
  const location = useLocation()

  const airfocusLogoSrc = blackBG
    ? 'airfocus-logos/airfocus-lucid-logo-white.webp'
    : // winter logo is already in images folder: "airfocus-logos/airfocus-logo-winter.webp"
      'airfocus-logos/airfocus-lucid-logo.webp'

  const pageLanguage = useLanguage()
  const footerIntroData = getFooterIntroData(pageLanguage)
  const footerOutroData = getFooterOutroData(pageLanguage)

  return (
    <footer
      id={'footer'}
      css={[footerStyle(withoutBadgeStrip, withoutNavigation, blackBG), cssStyles]}
      className={'full-screen-relative'}
    >
      {!withoutBadgeStrip && (
        <div className={'full-screen-relative'} css={badgeStripStyle(withoutNavigation, blackBG)}>
          <div css={badgeDescriptionStyle}>
            <div className={'heading4'}>{footerIntroData.topRatedTitle}</div>
            <div className={'body-1'}>{footerIntroData.topRatedSubtitle}</div>
          </div>
          <div css={badgeBlockStyle}>
            <div css={g2usersloveusImgStyle}>
              <Image src={'badges/g2-users-love-us.webp'} alt={'g2 badge users love us'} />
            </div>

            <div css={g2ImgStyle}>
              <Image src={'badges/g2-leader-winter.webp'} alt={'g2 badge leader winter 2025'} />
            </div>

            <div css={getappImgStyle}>
              <Image src={'badges/get-app.webp'} alt={'GetApp badge category leader'} />
            </div>

            <div css={saImgStyle}>
              <Image src={'badges/software-advice.webp'} alt={'software advice badge'} />
            </div>

            <div css={capterraImgStyle}>
              <Image src={'badges/capterra-shortlist.webp'} alt={'capterra shortlist badge'} />
            </div>

            <div css={crozdeskImgStyle}>
              <Image src={'badges/proddy.webp'} alt={'proddy roadmapping'} />
            </div>

            <div css={crozdeskImgStyle}>
              <Image src={'badges/crozdesk-quality-choice.webp'} alt={'crozdesk quality choice'} />
            </div>
          </div>
          <div css={coloredStrip(withoutBadgeStrip, blackBG)} />
        </div>
      )}

      {withoutBadgeStrip && <div css={coloredStrip(withoutBadgeStrip, blackBG)} />}

      {!withoutNavigation && (
        <div css={footerBodyStyle}>
          <div css={topBlockStyle}>
            <Link href={footerIntroData.url} aria-label={'Home'} className={'logo-link'}>
              <Image src={airfocusLogoSrc} alt={'airfocus-logo'} css={logoStyle} />
            </Link>
            <span className={'body-1'} css={subtitleStyle}>
              {parse(footerIntroData.heading)} &#128153;
            </span>
          </div>
          <div css={containerStyle(blackBG)}>
            {getFooterMenuItems(pageLanguage).map(({ columnName, columnItems }) => (
              <div css={menuColumnStyle} key={`Footer-column-${columnName}`}>
                <div className={'strong-body-1'}>{columnName}</div>
                <ul css={linksStyle} className={'body-1'}>
                  {columnItems
                    .filter(({ url }) => url !== location.pathname)
                    .map(({ title, url }) => (
                      <li key={`Footer-Entry-${columnName}-${title}`}>
                        <Link href={url}>{title}</Link>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className={'body-2 full-screen-relative'} css={footerBottomStyle(blackBG)}>
        <div css={emailStyle}>
          {footerOutroData.copyRight} {contactEmail}
        </div>
        <div css={legalLinksStyle}>
          {footerOutroData.outroItems.map(({ title, url }) => (
            <Link key={`Footer-Outro-${title}`} href={url}>
              {title}
            </Link>
          ))}
        </div>
        <div css={socialLinks}>
          <Link href={'https://www.linkedin.com/company/10393984/'} aria-label={'LinkedIn'}>
            <LinkedinIcon className={blackBG ? 'white-icon' : undefined} />
          </Link>
          <Link href={'https://www.youtube.com/c/airfocus/'} aria-label={'YouTube'}>
            <YouTubeIcon className={blackBG ? 'white-icon' : undefined} />
          </Link>
        </div>
        <div css={languageSelectStyle}>
          {footerOutroData.languages.map(({ language, url }, index) => (
            <>
              <Link href={url} aria-label={'Home'}>
                {language}
              </Link>
              {index !== footerOutroData.languages.length - 1 && (
                <VerticalTextDivider
                  height={'18px'}
                  width={'0.5px'}
                  color={'#909090'}
                  marginLeftRight={'0.25rem'}
                  translateX={'-0.25px'}
                  translateY={'2px'}
                />
              )}
            </>
          ))}
        </div>
      </div>
    </footer>
  )
}

export default Footer

const footerStyle = (withoutBadgeStrip: boolean, withoutNavigation: boolean, blackBG?: boolean) => css`
  ${withoutBadgeStrip && 'margin-top: 4.81rem'};
  ${withoutBadgeStrip && !withoutNavigation && 'padding-top: 4rem'};
  position: relative;
  width: 100%;
  background: ${blackBG ? '#071322' : '#fafbfc'};

  a {
    color: ${blackBG ? '#fff' : '#000'} !important;
    text-decoration: none;
    &:hover {
      ${blackBG
        ? `
          opacity: 0.65;
          `
        : `
          color: #318eff !important;
      `}
    }
  }
`

const badgeStripStyle = (withoutNavigation: boolean, blackBG: boolean) => css`
  flex-direction: column;
  align-items: center;
  position: relative;
  background-color: rgb(7, 19, 34);
  ${blackBG && 'border-top: 1px solid rgba(255, 255, 255, 0.2)'};
  color: #fff;
  padding: 2rem 0 2.5rem;
  margin: 4.81rem 0 ${withoutNavigation ? '0' : '4rem'};
  display: flex;
  gap: 1rem;

  .body-1 {
    margin: 0;
  }
`

const badgeDescriptionStyle = css`
  text-align: center;
  @media screen and (max-width: ${breakpoints.large}px) {
    margin-bottom: 1rem;

    .heading4,
    .body-1 {
      text-align: center;
    }
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    margin-bottom: 2rem;
  }
`

const badgeBlockStyle = css`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;
  gap: 1rem 3rem;

  @media screen and (max-width: ${breakpoints.medium}px) {
    column-gap: 1.5rem;
  }
`

const g2usersloveusImgStyle = css`
  width: 4.75rem;
`

const g2ImgStyle = css`
  width: 4.75rem;
`

const crozdeskImgStyle = css`
  width: 5.25rem;
`

const capterraImgStyle = css`
  width: 6.5rem;
  transform: translateY(2px);
`

const getappImgStyle = css`
  width: 6.5rem;
`

const saImgStyle = css`
  width: 5.25rem;
`

const coloredStrip = (withBadgeStrip: boolean, blackBG?: boolean) => css`
  position: absolute;
  ${!withBadgeStrip ? 'bottom: 0;' : ' top: 0;'};
  left: 0;
  width: 100%;
  height: ${blackBG ? '4px' : '8px'};
  background:
    url('/images/background-mesh-gradient.jpeg') center no-repeat,
    linear-gradient(135deg, #54d5eb, #266eea);
  background-size: cover;
  animation: footer-meshanimate 2s ease-in-out infinite;
  animation-direction: alternate;

  @keyframes footer-meshanimate {
    from {
      filter: hue-rotate(45deg);
    }
    to {
      filter: hue-rotate(1deg);
    }
  }
`

const footerBodyStyle = css`
  display: flex;
  flex-direction: column;
  position: relative;
`

const footerBottomStyle = (blackBG?: boolean) => css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  color: ${blackBG ? '#fff' : '#909090'} !important;
  padding: 1rem 0;
  align-items: center;
  line-height: 1.37rem;
  border-top: 1px solid ${blackBG ? 'rgba(255, 255, 255, 0.1)' : '#efeff0'};

  div > a {
    color: ${blackBG ? '#fff' : '#909090'} !important;
    display: block;
    padding: 0;
  }

  @media screen and (max-width: ${breakpoints.xlarge}px) {
    flex-direction: column;

    div {
      margin: 0.5rem auto;
    }
  }
`

const containerStyle = (blackBG: boolean) => css`
  padding: 3rem 0 4rem;
  display: grid;
  align-items: stretch;
  grid-gap: 0 3rem;
  grid-template-columns: repeat(4, 1fr);
  margin: 0 auto;
  border-top: 1px solid ${blackBG ? 'rgba(255, 255, 255, 0.1)' : '#efeff0'};

  @media screen and (max-width: ${breakpoints.large}px) {
    column-gap: 3%;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    grid-template-columns: 1fr 1fr;
    padding-top: 0;
    padding-bottom: 3rem;
    margin: 0;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    column-gap: 1.5rem;
  }
`

const menuColumnStyle = css`
  .strong-body-1 {
    margin-bottom: 1rem;

    @media screen and (max-width: ${breakpoints.medium}px) {
      padding-top: 1.87rem;
      margin-bottom: 1rem;
    }
  }
`

const linksStyle = css`
  list-style: none;
  margin: 0;
  padding: 0;
  text-align: left;
  height: 100%;

  li {
    display: block;
    margin: 0.5rem 0;
    padding: 0;
  }
`

const logoStyle = css`
  display: inline-block;
  width: 7.75rem;
  height: auto;
  width: 6.25rem;
`

const subtitleStyle = css`
  margin: 1rem 0 3rem 0 !important;
  max-width: 35rem;
  display: block;
  @media screen and (min-width: ${breakpoints.medium + 1}px) {
    margin-left: auto !important;
    margin-right: auto !important;
  }
`

const socialLinks = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 1.5rem;
  text-align: center;
  align-items: center;
  justify-content: space-between;

  a {
    width: 1rem;
    height: 1.3rem;
    cursor: pointer;
    display: flex;
    align-items: center;

    svg {
      height: 100%;
    }

    &:hover {
      opacity: 0.65;
    }
  }
`

const languageSelectStyle = css`
  display: flex;
`

const emailStyle = css`
  @media screen and (max-width: ${breakpoints.medium}px) {
    text-align: center;
  }
`

const legalLinksStyle = css`
  display: grid;
  grid-template-columns: auto auto auto auto;
  grid-gap: 1.25rem;
  align-items: center;

  @media screen and (max-width: ${breakpoints.large}px) {
    grid-gap: 0.8rem;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    grid-template-columns: auto auto auto auto;
    grid-gap: 1rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    grid-template-columns: auto;
    a {
      text-align: center;
    }
  }
`

const topBlockStyle = css`
  @media screen and (min-width: ${breakpoints.medium + 1}px) {
    text-align: center;
  }
`
